import { Graph } from '@square/ignition';
import {
  findGraph,
  extractGraphWebhook,
  extractGraphApi,
  extractGraphObject,
  extractGraphEndpoint,
  extractGraphEnum,
} from '@squareup/dex-data-access-oas';
import {
  useGraphEntitiesInView,
  useSelectSpecVersionInView,
} from '@squareup/dex-tech-store-access-selectors';
import { StateNode, SliceError } from '@squareup/dex-types-data-state';
import { useMemo } from 'react';

const graphNotFoundError = (version?: string | undefined): SliceError => ({
  data: `version: ${version || 'unknown'}`,
  error: 'Unable to find graph for version.',
  originalStatus: 404,
  status: 'PARSING_ERROR',
});

/**
 * This file contains hooks which select state from the store and aggregate that with what is in the graph to return a value.
 * This is required because the graph is not serializable and thus only the application state
 * (whats fetched, versions selected, does the entity exist etc) can be kept in the store. Not the entities themselves.
 */

const useGraph = () => {
  const { error, isLoading, parsedInfo } = useSelectSpecVersionInView();

  if (error) {
    return { error };
  }

  // Dont bother trying to find a graph if we have not parsed one.
  // If we arent loading but yet we have no version thats an error scenario.
  if (!parsedInfo && isLoading) {
    return { isLoading };
  }

  const version = parsedInfo?.version;
  const graph = findGraph(version);

  // This should never happen
  if (!graph) {
    return {
      error: graphNotFoundError(version),
    };
  }

  return { graph, isLoading: isLoading || false, error };
};

/**
 * Given a graph extractor, will extract the
 * corresponding graph entit(y/ies) from the graph.
 */
const useMemoGraphValue = <TResult = unknown>(
  graphExtractor: (graph: Graph | undefined) => TResult
): StateNode<TResult> => {
  const graphInfo = useGraph();
  const { graph, ...rest } = graphInfo;

  // Ignition entity listing is not stable so we memo.
  // NOTE: We are not including the graphExtractor in the deps by design, this function is used
  // across many hooks with different extractors. Each hook should recieve its stable result
  // until the graph changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const result = useMemo(() => graphExtractor(graph), [graph]);

  return { ...rest, ...result };
};

/**
 * Given a state selector for an entity and a graph extractor, will extract the
 * corresponding graph entit(y/ies) from the graph.
 */
const useGraphEntity = <TSelectorData = unknown, TGraphData = unknown>(
  useSelectorHook: () => StateNode<TSelectorData>,
  graphExtractor: <
    TStoreData extends Omit<
      ReturnType<typeof useSelectorHook>,
      'error' | 'isLoading'
    >
  >(
    _data: TStoreData,
    _graph: Graph
  ) => TGraphData,
  key: string
): StateNode<Record<string, TGraphData>> => {
  const {
    error: selectError,
    isLoading: isSelectLoading,
    ...selectorRest
  } = useSelectorHook();

  const { error: graphError, graph, isLoading: isGraphLoading } = useGraph();

  const isLoading = isGraphLoading || isSelectLoading;
  const error = graphError || selectError;

  let result: Record<string, TGraphData | undefined> = { [key]: undefined };

  if (!error && graph) {
    const object = graphExtractor(selectorRest, graph);

    result = { ...result, [key]: object };
  }

  return { ...result, isLoading, error } as StateNode<
    Record<string, TGraphData>
  >;
};

const useListGraphCategories = () =>
  useMemoGraphValue((graph) => ({
    categories: graph?.listCategories({ sort: true }) || [],
  }));

const useListGraphEntities = () =>
  useMemoGraphValue((graph) => ({
    entities: graph?.listEntities() || [],
  }));

const useListGraphWebhooks = () =>
  useMemoGraphValue((graph) => ({
    webhooks: graph?.listWebhooks() || [],
  }));

const useListGraphApis = () =>
  useMemoGraphValue((graph) => ({
    apis: graph?.listApis() || [],
  }));

const useListGraphEndpoints = () =>
  useMemoGraphValue((graph) => ({
    endpoints: graph?.listEndpoints() || [],
  }));

const useGraphApi = () =>
  useGraphEntity(useGraphEntitiesInView, extractGraphApi, 'api');

const useGraphObject = () =>
  useGraphEntity(useGraphEntitiesInView, extractGraphObject, 'object');

const useGraphEndpoint = () =>
  useGraphEntity(useGraphEntitiesInView, extractGraphEndpoint, 'endpoint');

const useGraphEnum = () =>
  useGraphEntity(useGraphEntitiesInView, extractGraphEnum, 'enum');

const useGraphWebhook = () =>
  useGraphEntity(useGraphEntitiesInView, extractGraphWebhook, 'webhook');

export {
  useGraph,
  useGraphApi,
  useGraphObject,
  useGraphEndpoint,
  useGraphEnum,
  useGraphWebhook,
  useListGraphEntities,
  useListGraphCategories,
  useListGraphWebhooks,
  useListGraphApis,
  useListGraphEndpoints,
};
